import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllProducts } from '../services/productService';
import { getCurrentUser, User } from '../services/authService';
import { getTransactionHistory, getCreditStats } from '../services/creditsService';

interface DashboardItem {
  id: number;
  title: string;
  price: number;
  date: string;
  imageUrl: string;
}

// Extend the User interface to include properties we need
interface ExtendedUser extends User {
  name?: string;
  joinDate?: string;
}

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const DashboardPage: React.FC = () => {
  const [recentPurchases, setRecentPurchases] = useState<DashboardItem[]>([]);
  const [downloadedItems, setDownloadedItems] = useState<DashboardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [creditStats, setCreditStats] = useState<{
    assetsCount: number;
    totalSpent: number;
    totalEarned: number;
    pendingEarnings: number;
  }>({
    assetsCount: 0,
    totalSpent: 0,
    totalEarned: 0,
    pendingEarnings: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        // Fetch user data
        const userData = await getCurrentUser();
        // Extend user data with additional properties
        const extendedUser: ExtendedUser = {
          ...userData as User,
          name: userData?.username || 'User',
          joinDate: 'January 2023'
        };
        setUser(extendedUser);

        // Fetch products for recent purchases and downloads
        const products = await getAllProducts();

        // Fetch transactions
        await getTransactionHistory();

        // Fetch credit stats
        const stats = await getCreditStats();
        setCreditStats(stats);

        // Transform products to dashboard items
        // In a real app, you would filter these based on user purchases and downloads
        const purchaseItems: DashboardItem[] = products
          .slice(0, 4) // Take first 4 as sample purchases
          .map(product => ({
            id: product.id,
            title: product.title,
            price: product.price,
            date: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            imageUrl: product.images[0] || 'https://via.placeholder.com/100'
          }));

        const downloadItems: DashboardItem[] = products
          .slice(4, 8) // Take next 4 as sample downloads
          .map(product => ({
            id: product.id,
            title: product.title,
            price: product.price,
            date: new Date(Date.now() - Math.floor(Math.random() * 60) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            imageUrl: product.images[0] || 'https://via.placeholder.com/100'
          }));

        setRecentPurchases(purchaseItems);
        setDownloadedItems(downloadItems);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7F56D9]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-6 mb-6 border border-white/5"
            >
              <div className="flex flex-col items-center text-center mb-6">
                <img src={user?.avatar || 'https://via.placeholder.com/150'} alt={user?.name || 'User'} className="w-24 h-24 rounded-full mb-4 border-2 border-[#F670C7]" />
                <h2 className="text-xl font-semibold text-white">{user?.name || 'Loading...'}</h2>
                <p className="text-white/70 text-sm">{user?.email || 'Loading...'}</p>
                <p className="text-white/50 text-xs mt-1">Member since {user?.joinDate || 'Loading...'}</p>
              </div>
              <div className="border-t border-white/10 pt-4">
                <nav className="space-y-2">
                  <a href="#" className="block px-4 py-2 rounded-md bg-[#F670C7]/20 text-[#F670C7] font-medium">Dashboard</a>
                  <a href="#" className="block px-4 py-2 rounded-md text-white/80 hover:bg-[#2A1E44]/70 transition duration-200">My Purchases</a>
                  <a href="#" className="block px-4 py-2 rounded-md text-white/80 hover:bg-[#2A1E44]/70 transition duration-200">Downloads</a>
                  <a href="#" className="block px-4 py-2 rounded-md text-white/80 hover:bg-[#2A1E44]/70 transition duration-200">Wishlist</a>
                  <a href="#" className="block px-4 py-2 rounded-md text-white/80 hover:bg-[#2A1E44]/70 transition duration-200">Account Settings</a>
                </nav>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-6 border border-white/5"
            >
              <h3 className="font-semibold text-white mb-4">Need Help?</h3>
              <p className="text-white/70 text-sm mb-4">
                Our support team is available 24/7 to assist you with any questions or issues.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full bg-[#2A1E44]/70 text-white/90 px-4 py-2 rounded-md hover:bg-[#2A1E44] transition duration-300"
              >
                Contact Support
              </motion.button>
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-6 mb-8 border border-white/5"
            >
              <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-[#7F56D9]/10 rounded-lg p-6 border border-[#7F56D9]/20">
                  <h3 className="font-semibold text-white mb-2">Total Purchases</h3>
                  <p className="text-3xl font-bold text-[#7F56D9]">{recentPurchases.length}</p>
                </div>
                <div className="bg-[#22C55E]/10 rounded-lg p-6 border border-[#22C55E]/20">
                  <h3 className="font-semibold text-white mb-2">Downloads</h3>
                  <p className="text-3xl font-bold text-[#22C55E]">{downloadedItems.length}</p>
                </div>
                <div className="bg-[#F670C7]/10 rounded-lg p-6 border border-[#F670C7]/20">
                  <h3 className="font-semibold text-white mb-2">Wishlist Items</h3>
                  <p className="text-3xl font-bold text-[#F670C7]">{creditStats.assetsCount}</p>
                </div>
              </div>

              <h2 className="text-xl font-semibold text-white mb-4">Recent Purchases</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-[#1A1625]/50 border border-white/5 rounded-lg">
                  <thead>
                    <tr className="bg-[#1A1625]/80">
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Product</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Date</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Price</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {recentPurchases.map(purchase => (
                      <tr key={purchase.id} className="hover:bg-white/5">
                        <td className="py-4 px-4 whitespace-nowrap text-sm font-medium text-white">{purchase.title}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-white/70">{purchase.date}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-white/70">{purchase.price}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-900/30 text-green-400">
                            Completed
                          </span>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-[#F670C7] hover:text-[#F670C7]/80">
                          <a href="#">Download</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-right">
                <a href="#" className="text-[#F670C7] hover:text-[#F670C7]/80 text-sm">View All Purchases →</a>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-6 border border-white/5"
            >
              <h2 className="text-xl font-semibold text-white mb-4">Downloaded Items</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-[#1A1625]/50 border border-white/5 rounded-lg">
                  <thead>
                    <tr className="bg-[#1A1625]/80">
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Product</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Downloads</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Last Download</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {downloadedItems.map(item => (
                      <tr key={item.id} className="hover:bg-white/5">
                        <td className="py-4 px-4 whitespace-nowrap text-sm font-medium text-white">{item.title}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-white/70">{item.price}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-white/70">{item.date}</td>
                        <td className="py-4 px-4 whitespace-nowrap text-sm text-[#F670C7] hover:text-[#F670C7]/80">
                          <a href="#">Download Again</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-right">
                <a href="#" className="text-[#F670C7] hover:text-[#F670C7]/80 text-sm">View All Downloads →</a>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
